import type { Vec2 } from "../core/types.ts";
import { COLORS } from "../core/constants.ts";
import { CELL_SIZE } from "../core/config.ts";

/** Callback fired when the path head reaches a tower cell */
export type DestroyCallback = (cell: Vec2) => void;

/** Pause duration when the path hits a tower (seconds) */
const DESTROY_PAUSE_DURATION = 0.35;
/** Speed: seconds per path segment */
const SECONDS_PER_SEGMENT = 0.06;

/**
 * PathRevealAnimation — Animates a path line progressively from spawn
 * to mainframe, like a "river filling" from start to finish.
 * Supports pause points at tower cells (CR 2.1 "blast" effect).
 * Rendered on the Canvas 2D layer during the spawn alert phase.
 */
export class PathRevealAnimation {
  private path: Vec2[] = [];
  private pathCells: Vec2[] = [];
  private progress = 0; // 0..totalSegments (not normalized)
  private active_ = false;
  private elapsed = 0;
  private onComplete: (() => void) | null = null;
  private onDestroy: DestroyCallback | null = null;

  // Destruction pause state
  private destroySet = new Set<string>();
  private paused_ = false;
  private pauseTimer = 0;
  private destroyedIndices = new Set<number>();
  private totalDuration = 0;

  get active(): boolean {
    return this.active_;
  }

  /**
   * Start the path reveal animation.
   * @param pathCells Grid-coordinate path from spawn to mainframe
   * @param destroyCells Grid cells occupied by towers (will trigger pause+destroy)
   * @param onDestroy Callback when a tower cell is reached
   * @param onComplete Callback when animation finishes
   */
  start(
    pathCells: Vec2[],
    destroyCells: Vec2[],
    onDestroy: DestroyCallback,
    onComplete: () => void,
  ): void {
    this.pathCells = pathCells;
    // Convert grid cells to pixel center coordinates
    this.path = pathCells.map((p) => ({
      x: p.x * CELL_SIZE + CELL_SIZE / 2,
      y: p.y * CELL_SIZE + CELL_SIZE / 2,
    }));
    this.progress = 0;
    this.elapsed = 0;
    this.active_ = true;
    this.paused_ = false;
    this.pauseTimer = 0;
    this.onComplete = onComplete;
    this.onDestroy = onDestroy;

    // Build set of destroy cell keys for O(1) lookup
    this.destroySet.clear();
    for (const c of destroyCells) {
      this.destroySet.add(`${c.x},${c.y}`);
    }
    this.destroyedIndices.clear();

    // Total duration scales with path length
    this.totalDuration = Math.max(
      1.5,
      (pathCells.length - 1) * SECONDS_PER_SEGMENT,
    );
  }

  update(dt: number): void {
    if (!this.active_) return;

    // Handle destruction pause
    if (this.paused_) {
      this.pauseTimer -= dt;
      if (this.pauseTimer <= 0) {
        this.paused_ = false;
      }
      return;
    }

    this.elapsed += dt;
    const totalSegments = this.path.length - 1;
    if (totalSegments <= 0) {
      this.finish();
      return;
    }

    const newProgress = Math.min(
      totalSegments,
      (this.elapsed / this.totalDuration) * totalSegments,
    );

    // Check if we've crossed a new segment boundary with a tower cell
    const prevSegment = Math.floor(this.progress);
    const newSegment = Math.floor(newProgress);

    for (let i = prevSegment + 1; i <= newSegment; i++) {
      if (i < this.pathCells.length && !this.destroyedIndices.has(i)) {
        const cell = this.pathCells[i]!;
        const key = `${cell.x},${cell.y}`;
        if (this.destroySet.has(key)) {
          this.destroyedIndices.add(i);
          // Trigger destruction callback + sound
          if (this.onDestroy) this.onDestroy(cell);
          // Pause the animation
          this.paused_ = true;
          this.pauseTimer = DESTROY_PAUSE_DURATION;
          this.progress = i;
          return; // Stop advancing — resume after pause
        }
      }
    }

    this.progress = newProgress;

    if (this.progress >= totalSegments) {
      this.finish();
    }
  }

  private finish(): void {
    this.active_ = false;
    this.progress = this.path.length - 1;
    if (this.onComplete) {
      const cb = this.onComplete;
      this.onComplete = null;
      cb();
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    if (!this.active_ || this.path.length < 2) return;

    const totalSegments = this.path.length - 1;
    const revealedLength = Math.min(this.progress, totalSegments);
    const fullSegments = Math.floor(revealedLength);
    const partialFraction = revealedLength - fullSegments;

    // Draw glow layer (wider, semi-transparent)
    ctx.save();
    ctx.lineWidth = 6;
    ctx.strokeStyle = `${COLORS.ORACLE_RED}60`;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(this.path[0]!.x, this.path[0]!.y);
    for (let i = 1; i <= fullSegments && i < this.path.length; i++) {
      ctx.lineTo(this.path[i]!.x, this.path[i]!.y);
    }
    if (fullSegments < totalSegments && partialFraction > 0) {
      const from = this.path[fullSegments]!;
      const to = this.path[fullSegments + 1]!;
      const x = from.x + (to.x - from.x) * partialFraction;
      const y = from.y + (to.y - from.y) * partialFraction;
      ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Draw main path line
    ctx.lineWidth = 3;
    ctx.strokeStyle = COLORS.ORACLE_RED;
    ctx.beginPath();
    ctx.moveTo(this.path[0]!.x, this.path[0]!.y);
    for (let i = 1; i <= fullSegments && i < this.path.length; i++) {
      ctx.lineTo(this.path[i]!.x, this.path[i]!.y);
    }
    if (fullSegments < totalSegments && partialFraction > 0) {
      const from = this.path[fullSegments]!;
      const to = this.path[fullSegments + 1]!;
      const x = from.x + (to.x - from.x) * partialFraction;
      const y = from.y + (to.y - from.y) * partialFraction;
      ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Draw head dot (pulsing at the current tip)
    if (this.progress < totalSegments) {
      let headX: number, headY: number;
      if (fullSegments < totalSegments && partialFraction > 0) {
        const from = this.path[fullSegments]!;
        const to = this.path[fullSegments + 1]!;
        headX = from.x + (to.x - from.x) * partialFraction;
        headY = from.y + (to.y - from.y) * partialFraction;
      } else {
        const last = this.path[Math.min(fullSegments, this.path.length - 1)]!;
        headX = last.x;
        headY = last.y;
      }
      const pulse = 0.5 + 0.5 * Math.sin(this.elapsed * 10);
      const radius = 4 + pulse * 2;
      ctx.beginPath();
      ctx.arc(headX, headY, radius, 0, Math.PI * 2);
      ctx.fillStyle = this.paused_ ? COLORS.ORACLE_RED : COLORS.AMBER_WARN;
      ctx.fill();

      // During pause: explosion ring effect
      if (this.paused_) {
        const expandProgress = 1 - this.pauseTimer / DESTROY_PAUSE_DURATION;
        const ringRadius = 8 + expandProgress * 16;
        ctx.beginPath();
        ctx.arc(headX, headY, ringRadius, 0, Math.PI * 2);
        ctx.strokeStyle = COLORS.ORACLE_RED;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 1 - expandProgress;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    ctx.restore();
  }

  reset(): void {
    this.active_ = false;
    this.progress = 0;
    this.elapsed = 0;
    this.path = [];
    this.pathCells = [];
    this.onComplete = null;
    this.onDestroy = null;
    this.paused_ = false;
    this.pauseTimer = 0;
    this.destroySet.clear();
    this.destroyedIndices.clear();
  }
}
